import React, { createContext, useContext, useEffect, useState } from 'react';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApplicationSessionId, getBrowserSessionId } from '@zep/session-id';
import { ZepApiResponse } from './types/ZepApiResponse';
import { getTraceId } from './util/getTraceId';
import { ZepApiError } from './ZepApiError';

// Axios 에러는 xhr onloadend 콜백에서 생성돼 실제 caller(queryFn 등) 스택이 유실된다.
// message에 method/URL/status를 주입해 Sentry 이슈 타이틀만 봐도 어느 API 호출인지 식별 가능하게 하고,
// httpStatus/status 필드를 붙여 Sentry의 ApiErrorLike 노이즈 필터(401/403/404/422)에 걸리게 한다.
// status 는 "METHOD URL" 형태로 채워서 fingerprint([name, httpStatus, status])가 엔드포인트별로 분리되게 한다.
type AnnotatedAxiosError = Omit<AxiosError, 'status'> & {
  httpStatus: number;
  status: string;
};
const annotateAxiosError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return error;
  const axiosError = error as AxiosError;
  const method = axiosError.config?.method?.toUpperCase() ?? 'UNKNOWN';
  const url = axiosError.config?.url ?? 'unknown-url';
  const httpStatus = axiosError.response?.status;
  const prefix = `[${method} ${url}${httpStatus ? ` ${httpStatus}` : ''}]`;
  if (!axiosError.message.startsWith(prefix)) {
    axiosError.message = `${prefix} ${axiosError.message}`;
  }
  if (typeof httpStatus === 'number') {
    const annotated = axiosError as unknown as AnnotatedAxiosError;
    annotated.httpStatus = httpStatus;
    annotated.status = `${method} ${url}`;
  }
  return axiosError;
};
export const createApiClient = (axiosConfig?: AxiosRequestConfig) => {
  const client = axios.create(axiosConfig);
  client.interceptors.response.use((response: AxiosResponse<ZepApiResponse>) => {
    if (response.data.status !== undefined && response.data.status < 0) {
      throw ZepApiError.createErrorFromResponse(response.data);
    }
    return response;
  }, error => Promise.reject(annotateAxiosError(error)));
  client.interceptors.request.use(config => {
    config.headers['X-ZEP-Trace-Id'] = getTraceId();
    return config;
  });
  return client;
};
export const defaultAxiosConfig = {
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'ngrok-skip-browser-warning': 'true',
    'X-ZEP-Application-Session-Id': getApplicationSessionId(),
    'X-ZEP-Browser-Session-Id': getBrowserSessionId()
  }
};
const ZepApiClientContext = createContext<AxiosInstance>(createApiClient());
export const ZepApiProvider = (props: ZEPApiProviderProps) => {
  const {
    axiosConfig,
    children
  } = props;
  const [apiClient, setApiClient] = useState(() => createApiClient(axiosConfig));
  useEffect(() => {
    setApiClient(() => createApiClient(axiosConfig));
  }, [axiosConfig]);
  return <ZepApiClientContext.Provider value={apiClient} data-sentry-element="unknown" data-sentry-component="ZepApiProvider" data-sentry-source-file="ZepApiProvider.tsx">
      {children}
    </ZepApiClientContext.Provider>;
};
export const useZepApiClient = () => {
  return useContext(ZepApiClientContext);
};
export interface ZEPApiProviderProps {
  axiosConfig: AxiosRequestConfig;
  children: React.ReactNode;
}